import {
  IBundleTypesService,
  IBundleUpgradeInfo,
} from "./bundle-types.service.interface";
import { ServiceBase } from "../service-base";
import { IBundleType } from "./bundle-type.interface";
import { IServiceFactory } from "../service-factory.interface";
import {
  BundleCodes,
  IBundleTypesConfiguration,
} from "../bundle-types-configuration/bundle-types-configuration.service.interface";
import { OldBundleType } from "./old-bundle-type";
import { IBundleViewModel } from "../booking/models/bundle/bundle-view-model.interface";

export class BundleTypesService
  extends ServiceBase
  implements IBundleTypesService
{
  constructor(services: IServiceFactory) {
    super(services);
    this._bundlesDisplayIndex = this._configuration.bundlesDisplayOrder.reduce(
      (accumulator: Record<string, number>, bundleType, index) => {
        accumulator[bundleType.code] = index;
        return accumulator;
      },
      {}
    );
  }

  private readonly _bundlesDisplayIndex: Record<string, number> = {};

  private get _configuration(): IBundleTypesConfiguration {
    return this.services.bundleTypesConfiguration.getConfiguration();
  }

  getBundleType(bundleCode: string): IBundleType {
    for (let bundleType of this._configuration.bundlesDisplayOrder) {
      if (bundleType.code === bundleCode) {
        return bundleType;
      }
    }

    return new OldBundleType(
      bundleCode,
      this.services.configuration.noBundleEarliestCheckIn,
      this.getLowestBundleType().earliestCheckInWhenHasSeat,
      this.services
    );
  }

  getLowestBundleType(): IBundleType {
    return this.getBundleType(BundleCodes.ABAS);
  }

  sortBundlesByDisplayOrder(bundles: IBundleViewModel[]): IBundleViewModel[] {
    return bundles.sort((b1, b2) => {
      return (
        this._bundlesDisplayIndex[b1.bundleCode] -
        this._bundlesDisplayIndex[b2.bundleCode]
      );
    });
  }

  getBundleUpgradeInfo(
    upgradeFromBundle: IBundleType,
    allAvailableBundles: IBundleType[]
  ): IBundleUpgradeInfo | null {
    const bundlesUpgrades: Record<
      string,
      Record<string, IBundleUpgradeInfo>
    > = {
      [BundleCodes.ABAS]: {
        [BundleCodes.BPLU]: this._getAbasToBpluUpgradeInfo(),
      },
      [BundleCodes.BPLU]: {
        [BundleCodes.CBIZ]: this._getBpluToBizUpgradeInfo(),
        [BundleCodes.CBCM]: this._getBpluToCbcmUpgradeInfo(),
      },
      [BundleCodes.FBAS]: {
        [BundleCodes.FPLU]: this._getFbasToFpluUpgradeInfo(),
      },
      [BundleCodes.FPLU]: {
        [BundleCodes.FBIZ]: this._getFpluToFbizUpgradeInfo(),
      },
    };

    const possibleUpgrades = bundlesUpgrades[upgradeFromBundle.code];
    if (!possibleUpgrades) {
      return null;
    }

    for (let availableBundle of allAvailableBundles) {
      if (possibleUpgrades[availableBundle.code]) {
        return possibleUpgrades[availableBundle.code];
      }
    }

    return null;
  }
  /*
    private _getSbagIcon() {
        return (
            <SbagSsrSmallIcon size={SSR_LARGE_ICON_SIZE}/>
        )
    }
*/
  private _getAbasToBpluUpgradeInfo(): IBundleUpgradeInfo {
    return {
      upgradeTo: this.getBundleType(BundleCodes.BPLU),
      benefits: [
        {
          description: this.services.ssrTypes.SCBG.name,
          details: this.services.ssrTypes.SCBG.details,
          icon: this.services.ssrTypes.SCBG.largeIcon,
          from: true,
          to: true,
        },
        {
          description: this.services.ssrTypes.STST.name,
          details: this.services.ssrTypes.STST.details,
          icon: this.services.ssrTypes.STST.largeIcon,
          from: false,
          to: true,
        },
        {
          description: this.services.ssrTypes.PBRD.name,
          details: this.services.ssrTypes.PBRD.details,
          icon: this.services.ssrTypes.PBRD.largeIcon,
          from: false,
          to: true,
        },
        {
          description: this.services.ssrTypes.BCBG.name,
          details: this.services.ssrTypes.BCBG.details,
          icon: this.services.ssrTypes.BCBG.largeIcon,
          from: false,
          to: true,
        },
        {
          description: this.services.ssrTypes.CKN.name,
          details: null,
          icon: this.services.ssrTypes.CKN.largeIcon,
          from: false,
          to: true,
        },
      ],
    };
  }

  private _getBpluToBizUpgradeInfo(): IBundleUpgradeInfo {
    return {
      upgradeTo: this.getBundleType(BundleCodes.CBIZ),
      benefits: [
        {
          description: this.services.ssrTypes.SCBG.name,
          details: this.services.ssrTypes.SCBG.details,
          icon: this.services.ssrTypes.SCBG.largeIcon,
          from: true,
          to: true,
        },
        {
          description: this.services.ssrTypes.STEX.name,
          details: this.services.ssrTypes.STEX.details,
          icon: this.services.ssrTypes.STEX.largeIcon,
          from: false,
          to: true,
        },
        {
          description: this.services.ssrTypes.PBRD.name,
          details: this.services.ssrTypes.PBRD.details,
          icon: this.services.ssrTypes.PBRD.largeIcon,
          from: true,
          to: true,
        },
        {
          description: this.services.ssrTypes.BCBG.name,
          details: this.services.ssrTypes.BCBG.details,
          icon: this.services.ssrTypes.BCBG.largeIcon,
          from: true,
          to: true,
        },
        {
          description: this.services.ssrTypes.BAG.name,
          details: this.services.ssrTypes.BAG.details,
          icon: this.services.ssrTypes.BAG.largeIcon,
          from: false,
          to: true,
        },
        {
          description: this.services.ssrTypes.FLX.name,
          details: this.services.ssrTypes.FLX.details,
          icon: this.services.ssrTypes.FLX.largeIcon,
          from: false,
          to: true,
        },
        {
          description: this.services.ssrTypes.CKN.name,
          details: null,
          icon: this.services.ssrTypes.CKN.largeIcon,
          from: true,
          to: true,
        },
      ],
    };
  }

  private _getBpluToCbcmUpgradeInfo(): IBundleUpgradeInfo {
    const toBizUpgradeInfo = this._getBpluToBizUpgradeInfo();
    return {
      ...toBizUpgradeInfo,
      upgradeTo: this.getBundleType(BundleCodes.CBCM),
      benefits: [
        ...toBizUpgradeInfo.benefits,
        {
          description: this.services.ssrTypes.FAST.name,
          details: this.services.ssrTypes.FAST.details,
          icon: this.services.ssrTypes.FAST.largeIcon,
          from: false,
          to: true,
        },
        {
          description: this.services.ssrTypes.LOU.name,
          details: this.services.ssrTypes.LOU.details,
          icon: this.services.ssrTypes.LOU.largeIcon,
          from: false,
          to: true,
        },
        {
          description: this.services.ssrTypes.SBUS.name,
          details: this.services.ssrTypes.SBUS.details,
          icon: this.services.ssrTypes.SBUS.largeIcon,
          from: false,
          to: true,
        },
      ],
    };
  }

  private _getFbasToFpluUpgradeInfo(): IBundleUpgradeInfo {
    return {
      upgradeTo: this.getBundleType(BundleCodes.FPLU),
      benefits: [
        {
          description: this.services.ssrTypes.SCBG.name,
          details: this.services.ssrTypes.SCBG.details,
          icon: this.services.ssrTypes.SCBG.largeIcon,
          from: true,
          to: true,
        },
        {
          description: this.services.ssrTypes.STEX.name,
          details: this.services.ssrTypes.STEX.details,
          icon: this.services.ssrTypes.STEX.largeIcon,
          from: false,
          to: true,
        },
        {
          description: this.services.ssrTypes.PBRD.name,
          details: this.services.ssrTypes.PBRD.details,
          icon: this.services.ssrTypes.PBRD.largeIcon,
          from: true,
          to: true,
        },
        {
          description: this.services.ssrTypes.BCBG.name,
          details: this.services.ssrTypes.BCBG.details,
          icon: this.services.ssrTypes.BCBG.largeIcon,
          from: true,
          to: true,
        },
        {
          description: this.services.ssrTypes.BAG.name,
          details: this.services.ssrTypes.BAG.details,
          icon: this.services.ssrTypes.BAG.largeIcon,
          from: false,
          to: true,
        },
        {
          description: this.services.ssrTypes.FLX.name,
          details: this.services.ssrTypes.FLX.details,
          icon: this.services.ssrTypes.FLX.largeIcon,
          from: false,
          to: true,
        },
        {
          description: this.services.ssrTypes.CKN.name,
          details: null,
          icon: this.services.ssrTypes.CKN.largeIcon,
          from: true,
          to: true,
        },
        {
          description: this.services.ssrTypes.FAST.name,
          details: this.services.ssrTypes.FAST.details,
          icon: this.services.ssrTypes.FAST.largeIcon,
          from: true,
          to: true,
        },
      ],
    };
  }

  private _getFpluToFbizUpgradeInfo(): IBundleUpgradeInfo {
    return {
      upgradeTo: this.getBundleType(BundleCodes.FBIZ),
      benefits: [
        {
          description: this.services.ssrTypes.SCBG.name,
          details: this.services.ssrTypes.SCBG.details,
          icon: this.services.ssrTypes.SCBG.largeIcon,
          from: true,
          to: true,
        },
        {
          description: this.services.ssrTypes.STEX.name,
          details: this.services.ssrTypes.STEX.details,
          icon: this.services.ssrTypes.STEX.largeIcon,
          from: false,
          to: true,
        },
        {
          description: this.services.ssrTypes.PBRD.name,
          details: this.services.ssrTypes.PBRD.details,
          icon: this.services.ssrTypes.PBRD.largeIcon,
          from: true,
          to: true,
        },
        {
          description: this.services.ssrTypes.BCBG.name,
          details: this.services.ssrTypes.BCBG.details,
          icon: this.services.ssrTypes.BCBG.largeIcon,
          from: true,
          to: true,
        },
        {
          description: this.services.ssrTypes.BAG.name,
          details: this.services.ssrTypes.BAG.details,
          icon: this.services.ssrTypes.BAG.largeIcon,
          from: false,
          to: true,
        },
        {
          description: this.services.ssrTypes.FLX.name,
          details: this.services.ssrTypes.FLX.details,
          icon: this.services.ssrTypes.FLX.largeIcon,
          from: false,
          to: true,
        },
        {
          description: this.services.ssrTypes.FAST.name,
          details: this.services.ssrTypes.FAST.details,
          icon: this.services.ssrTypes.FAST.largeIcon,
          from: true,
          to: true,
        },
        {
          description: this.services.ssrTypes.CKN.name,
          details: null,
          icon: this.services.ssrTypes.CKN.largeIcon,
          from: true,
          to: true,
        },
      ],
    };
  }
}
